import type { ServerMessage } from '@/types/generated/public_api'

export type StreamMessageListener = (message: ServerMessage) => void

interface ListenerBucket {
  unscoped: Set<StreamMessageListener>
  byScope: Map<string, Set<StreamMessageListener>>
}

export type ScopedListenerRegistry = Map<string, ListenerBucket>

const scopedMarketChannelKeys = new Set([
  'marketDataStream:tickers',
  'marketDataStream:incrementalTickers',
  'marketDataStream:orderbook',
  'marketDataStream:trades',
])

export const marketDataListenerScope = (exchange: number, symbol: string) => `${exchange}:${symbol}`

export function listenerScopesForPayloads(
  channelKey: string,
  payloads: readonly unknown[]
): string[] | undefined {
  if (!scopedMarketChannelKeys.has(channelKey)) return undefined

  const scopes = new Set<string>()
  for (const payload of payloads) {
    if (!payload || typeof payload !== 'object') continue
    const candidate = payload as { exchange?: unknown; symbol?: unknown }
    if (typeof candidate.exchange !== 'number' || typeof candidate.symbol !== 'string') continue
    scopes.add(marketDataListenerScope(candidate.exchange, candidate.symbol))
  }
  return scopes.size > 0 ? Array.from(scopes) : undefined
}

export function messageListenerScopes(message: ServerMessage, channelKey: string): string[] {
  const data = message.data
  if (!data) return []

  let rows: readonly { exchange: number; symbol: string }[] = []
  if (channelKey === 'marketDataStream:tickers') rows = data.tickers?.tickers ?? []
  else if (channelKey === 'marketDataStream:incrementalTickers') {
    rows = data.incrementalTickers?.incrementalTickers ?? []
  } else if (channelKey === 'marketDataStream:orderbook') {
    rows = data.orderBooks?.orderBooks ?? []
  } else if (channelKey === 'marketDataStream:trades') {
    rows = data.instrumentTrades?.instrumentTrades ?? []
  }

  const scopes = new Set<string>()
  for (const row of rows) scopes.add(marketDataListenerScope(row.exchange, row.symbol))
  return Array.from(scopes)
}

function getOrCreateBucket(registry: ScopedListenerRegistry, channelKey: string): ListenerBucket {
  const existing = registry.get(channelKey)
  if (existing) return existing
  const bucket: ListenerBucket = { unscoped: new Set(), byScope: new Map() }
  registry.set(channelKey, bucket)
  return bucket
}

export function addScopedListener(
  registry: ScopedListenerRegistry,
  channelKey: string,
  listener: StreamMessageListener,
  scopes?: readonly string[]
): void {
  const bucket = getOrCreateBucket(registry, channelKey)
  if (!scopes?.length) {
    bucket.unscoped.add(listener)
    return
  }
  for (const scope of scopes) {
    const listeners = bucket.byScope.get(scope) ?? new Set<StreamMessageListener>()
    listeners.add(listener)
    bucket.byScope.set(scope, listeners)
  }
}

export function removeScopedListener(
  registry: ScopedListenerRegistry,
  channelKey: string,
  listener: StreamMessageListener,
  scopes?: readonly string[]
): void {
  const bucket = registry.get(channelKey)
  if (!bucket) return

  if (!scopes?.length) bucket.unscoped.delete(listener)
  else {
    for (const scope of scopes) {
      const listeners = bucket.byScope.get(scope)
      listeners?.delete(listener)
      if (listeners?.size === 0) bucket.byScope.delete(scope)
    }
  }

  if (bucket.unscoped.size === 0 && bucket.byScope.size === 0) registry.delete(channelKey)
}

export function collectScopedListeners(
  registry: ScopedListenerRegistry,
  channelKey: string,
  message: ServerMessage
): Set<StreamMessageListener> {
  const bucket = registry.get(channelKey)
  if (!bucket) return new Set()

  const listeners = new Set(bucket.unscoped)
  for (const scope of messageListenerScopes(message, channelKey)) {
    bucket.byScope.get(scope)?.forEach((listener) => listeners.add(listener))
  }
  return listeners
}

export function countScopedListeners(registry: ScopedListenerRegistry): number {
  const unique = new Set<StreamMessageListener>()
  registry.forEach((bucket) => {
    bucket.unscoped.forEach((listener) => unique.add(listener))
    bucket.byScope.forEach((listeners) => listeners.forEach((listener) => unique.add(listener)))
  })
  return unique.size
}
